/* Soolla Warp Hero — production engine (vanilla JS, zero dependencies)
   ====================================================================
   This is a 3D FLY-THROUGH, not a falling animation. Pouches spawn far
   away in a perspective-projected field, rush TOWARD the camera (growing
   as they approach), and exit off the edges before respawning deep.

   DO NOT REIMPLEMENT THIS EFFECT. Use this file verbatim.

   Usage:
     var warp = SoollaWarp.mount(containerEl, {
       images: [...],   // required: array of image URLs (transparent PNGs)
       speed: 0.9,      // approved production value
       count: 56,       // approved production value
       size:  1.45,     // approved production value
       blur:  true      // approved production value (motion-blur ghosts)
     });
     warp.update({ speed: 1.2 });  // live-retune any option
     warp.destroy();               // stop + remove canvas

   The container must be position:relative (or absolute/fixed) — the
   canvas is injected as an absolutely-positioned, full-bleed child.
   Respects prefers-reduced-motion: renders a static scattered field. */

(function () {
  'use strict';

  var Z_NEAR = 0.055;   // kill plane — pouch has "passed" the camera
  var Z_FAR  = 1.0;     // spawn depth
  var FADE_Z = 0.16;    // begin fly-by fade below this depth

  function rand(a, b) { return a + Math.random() * (b - a); }

  function mount(container, options) {
    var opts = {
      speed: 0.9,
      count: 56,
      size: 1.45,
      blur: true,
      maxDpr: 2,     // P-07: device-pixel-ratio cap (mobile mounts pass 1.5)
      // Batch 3B: image streaming. When `stream` is on, only `initialImages`
      // of the distinct image URLs are fetched up front; the animation starts
      // once those have decoded and the rest are streamed in, `streamBatch` at
      // a time, on idle, joining the field as particles respawn.
      stream: false,
      initialImages: 10,
      streamBatch: 3,        // urls released per wave
      streamInterval: 100,   // ms between waves — waves OVERLAP on the wire
      streamStartDelay: 250, // release the rest at the latest this long after
                             // mount, even if the first batch is still in
                             // flight, so a slow link never leaves the pipe
                             // idle (the first 10 keep their head start)
      streamTimeout: 1800,   // start anyway if the first batch is slow
      images: []
    };
    var key;
    for (key in options) { if (Object.prototype.hasOwnProperty.call(options, key)) opts[key] = options[key]; }
    if (!opts.images.length) { throw new Error('SoollaWarp.mount: options.images is required'); }

    var canvas = document.createElement('canvas');
    canvas.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;display:block;pointer-events:none;';
    container.appendChild(canvas);
    var ctx = canvas.getContext('2d');

    var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    var particles = [];
    var images = [];
    var raf = 0;
    var w = 0, h = 0, dpr = 1;
    var destroyed = false;
    var inViewport = true;
    var pageVisible = !document.hidden;
    var intersectionObserver = null;

    // Batch 3B streaming state
    var streaming = !!opts.stream && !reduced;
    var imgByUrl = null;      // url -> shared Image (dedupes the doubled list)
    var uniqUrls = [];        // distinct urls, first-appearance order
    var readySlots = [];      // indices into `images` whose Image has decoded
    var streamQueue = [];     // urls still to fetch
    var onImageSettled = null;// set by loadImages: may release the start gate
    var started = !streaming; // has the animation been allowed to start?
    var readyUnique = 0;
    var streamTimer = 0;

    function whenIdle(fn, timeout) {
      if (typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback(fn, { timeout: timeout || 500 });
      } else {
        window.setTimeout(fn, 1);
      }
    }

    function markReady(img) {
      if (img.__warpReady) return;
      img.__warpReady = true;
      readyUnique += 1;
      for (var i = 0; i < images.length; i++) {
        if (images[i] === img) readySlots.push(i);
      }
    }

    // Fetch one url. `onDone` fires on decode OR error, never twice.
    function beginLoad(src, onDone) {
      var img = imgByUrl[src];
      if (!img) { if (onDone) onDone(); return; }
      if (img.__warpStarted) { if (onDone) onDone(); return; }
      img.__warpStarted = true;
      var settled = false;
      function finish() {
        if (settled) return;
        settled = true;
        if (img.complete && img.naturalWidth) markReady(img);
        if (onDone) onDone();
      }
      img.onload = function () {
        // decode() keeps the first rasterisation off the frame that draws it
        if (typeof img.decode === 'function') {
          try { img.decode().then(finish, finish); } catch (e) { finish(); }
        } else {
          finish();
        }
      };
      img.onerror = finish;
      img.src = src;
    }

    // Deterministic spread over the ordered distinct list: slot i takes
    // index round(i * total / n). For 26 images and n=10 that is
    // 0,3,5,8,10,13,16,18,21,23 — every ~2.6th colourway, so the opening
    // field is balanced across the whole range instead of the first ten.
    function pickInitial(total, n) {
      var out = [], taken = {}, i, idx, guard;
      n = Math.max(1, Math.min(n | 0, total));
      for (i = 0; i < n; i++) {
        idx = Math.round(i * total / n);
        if (idx >= total) idx = total - 1;
        guard = 0;
        while (taken[idx] && guard < total) { idx = (idx + 1) % total; guard++; }
        taken[idx] = true;
        out.push(idx);
      }
      return out;
    }

    function loadImages(list) {
      imgByUrl = {};
      uniqUrls = [];
      readySlots = [];
      streamQueue = [];
      readyUnique = 0;
      if (streamTimer) { window.clearTimeout(streamTimer); streamTimer = 0; }
      images = list.map(function (src) {
        var img = imgByUrl[src];
        if (!img) {
          img = new Image();
          imgByUrl[src] = img;
          uniqUrls.push(src);
        }
        return img;
      });

      if (!streaming) {
        // unchanged behaviour: every url requested at mount, no handlers, no
        // decode(); particles draw as soon as each Image reports complete.
        uniqUrls.forEach(function (src) {
          var im = imgByUrl[src];
          im.__warpStarted = true;
          im.src = src;
        });
        return;
      }

      var initial = pickInitial(uniqUrls.length, opts.initialImages);
      var inInitial = {};
      initial.forEach(function (i) { inInitial[i] = true; });
      uniqUrls.forEach(function (src, i) { if (!inInitial[i]) streamQueue.push(src); });

      var pending = initial.length;
      var forced = false;
      // If the first batch has not landed by `streamTimeout`, stop insisting
      // on all of it: the animation then starts on whatever decodes next, so
      // a bandwidth-bound phone is never left staring at an empty hero. The
      // field is briefly built from fewer colourways and fills as they arrive.
      var timer = window.setTimeout(function () { timer = 0; forced = true; release(); }, opts.streamTimeout);
      function release() {
        if (started || destroyed) return;
        if (!readyUnique) return;   // nothing to draw at all yet — keep waiting
        if (!forced && readyUnique < initial.length) return;
        if (timer) { window.clearTimeout(timer); timer = 0; }
        started = true;
        ensureCount(opts.count);
        syncAnimation();
        // let the first frames land before touching the network again
        if (streamTimer) { window.clearTimeout(streamTimer); streamTimer = 0; }
        whenIdle(pumpStream, 300);
      }
      // Once the timeout has forced the gate open, ANY image landing — initial
      // or streamed — is enough to start the field.
      onImageSettled = function () { release(); };
      initial.forEach(function (i) {
        beginLoad(uniqUrls[i], function () {
          pending -= 1;
          release();
        });
      });
      if (!initial.length) { started = true; }
      // Failsafe: on a bandwidth-bound link the first batch can take seconds.
      // Waiting for `started` before asking for anything else leaves the
      // connection idle and pushes the full field far out, so release the
      // rest on a timer too — whichever comes first.
      if (streamQueue.length && !streamTimer) {
        streamTimer = window.setTimeout(pumpStream, opts.streamStartDelay);
      }
    }

    // Release the remaining urls in small waves. Waves are scheduled on a
    // short timer rather than on the previous wave *completing* — chaining on
    // completion serialises 6 round trips on a high-latency link and made the
    // full field land later than loading everything at once. Each wave is
    // small enough that its decodes never land on one frame, and the requests
    // still overlap on the wire.
    function pumpStream() {
      streamTimer = 0;
      if (destroyed || !streamQueue.length) return;
      var batch = streamQueue.splice(0, Math.max(1, opts.streamBatch | 0));
      batch.forEach(function (src) { beginLoad(src, onImageSettled); });
      if (streamQueue.length) {
        streamTimer = window.setTimeout(pumpStream, Math.max(0, opts.streamInterval | 0));
      }
    }

    function pickImage() {
      if (!streaming) return images[(Math.random() * images.length) | 0] || null;
      if (!readySlots.length) return null;
      return images[readySlots[(Math.random() * readySlots.length) | 0]] || null;
    }

    loadImages(opts.images);

    function fov() { return Math.min(w, h) * 1.05; }

    function spawn(p, initial) {
      // Pick a screen-uniform position at spawn depth, then back-project to
      // world space — spreads pouches across the WHOLE viewport at every
      // depth. No center-clump, no starburst, and definitely no raining.
      p.z = initial ? rand(0.3, Z_FAR) : rand(0.88, Z_FAR);
      var sx = rand(-0.55, 0.55) * w;
      var sy = rand(-0.55, 0.55) * h;
      var f = fov();
      p.x = sx * p.z / f;                  // world units
      p.y = sy * p.z / f;
      p.vz = rand(0.16, 0.44);             // forward speed (depth/sec) — toward camera
      p.vx = rand(-0.035, 0.035);          // slight lateral drift only
      p.vy = rand(-0.03, 0.03);
      p.size = rand(0.028, 0.06);          // world size
      p.rot = rand(-0.5, 0.5);
      p.spin = rand(-0.55, 0.55);          // rad/sec
      p.img = pickImage();
      p.delay = initial ? rand(0, 1.2) : rand(0.1, 1.1);
      p.age = 0;
      p.psx = null; p.psy = null;          // previous screen pos (for blur ghosts)
    }

    function ensureCount(n) {
      while (particles.length < n) {
        var p = {};
        spawn(p, true);
        particles.push(p);
      }
      if (particles.length > n) particles.length = n;
    }

    function resize() {
      dpr = Math.min(window.devicePixelRatio || 1, opts.maxDpr || 2);
      w = canvas.clientWidth;
      h = canvas.clientHeight;
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
    }
    resize();
    // When streaming, particles are created only once at least one image has
    // decoded — otherwise they would be born with no image, age out their
    // ease-in off-screen and pop in at full opacity when one finally arrives.
    if (!streaming || readySlots.length) ensureCount(opts.count);
    window.addEventListener('resize', resize);

    function drawPouch(p, x, y, pw, ph, a) {
      ctx.save();
      ctx.globalAlpha = Math.min(a, 1);
      ctx.translate(x, y);
      ctx.rotate(p.rot);
      ctx.drawImage(p.img, -pw / 2, -ph / 2, pw, ph);
      ctx.restore();
    }

    function paint(dt, animate) {
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, w, h);

      var f = fov();
      var cx = w / 2, cy = h / 2;
      var margin = 1.4;

      // far-to-near paint order so close pouches overlap distant ones
      var order = particles.slice().sort(function (a, b) { return b.z - a.z; });

      for (var i = 0; i < order.length; i++) {
        var p = order[i];
        if (animate) {
          if (p.delay > 0) { p.delay -= dt; continue; }
          p.age += dt;
          p.z -= p.vz * opts.speed * dt;   // depth DECREASES → flies toward viewer
          p.x += p.vx * dt;
          p.y += p.vy * dt;
          p.rot += p.spin * dt;
        }

        var scale = f / p.z;               // perspective projection
        var sx = cx + p.x * scale;
        var sy = cy + p.y * scale;
        var px = p.size * scale * opts.size;   // on-screen width grows as z shrinks

        // exit: passed the camera, or fully off any edge / corner
        if (p.z <= Z_NEAR ||
            sx < -px * margin || sx > w + px * margin ||
            sy < -px * margin || sy > h + px * margin) {
          spawn(p, false);
          continue;
        }
        if (!p.img || !p.img.complete || !p.img.naturalWidth) continue;

        // opacity: ease in from a speck, hold, fade on the fly-by
        var fadeIn = animate ? Math.min(p.age / 0.45, 1) : 1;
        var fadeOut = p.z < FADE_Z
          ? Math.max((p.z - Z_NEAR) / (FADE_Z - Z_NEAR), 0)
          : 1;
        var depthIn = 0.35 + 0.65 * Math.min((Z_FAR - p.z) / 0.25, 1);
        var alpha = fadeIn * fadeOut * depthIn;
        if (alpha <= 0.01) continue;

        var ph = px * (p.img.naturalHeight / p.img.naturalWidth);

        // motion blur: ghost copies trailing along the screen velocity
        if (animate && opts.blur && p.psx !== null) {
          var dx = sx - p.psx, dy = sy - p.psy;
          var dist = Math.sqrt(dx * dx + dy * dy);
          if (dist > 6) {
            var ghosts = dist > 26 ? 3 : 2;
            for (var g = ghosts; g >= 1; g--) {
              var t = g / (ghosts + 1);
              drawPouch(p, sx - dx * t * 1.6, sy - dy * t * 1.6, px, ph,
                        alpha * 0.16 / g);
            }
          }
        }
        drawPouch(p, sx, sy, px, ph, alpha);
        p.psx = sx; p.psy = sy;
      }
    }

    var last = performance.now();
    function frame(now) {
      raf = 0;
      if (destroyed || !inViewport || !pageVisible) return;
      var dt = Math.min((now - last) / 1000, 0.05);
      last = now;
      ensureCount(opts.count);
      paint(dt, true);
      raf = requestAnimationFrame(frame);
    }

    function syncAnimation() {
      if (destroyed || reduced) return;
      if (!started) return;   // streaming: hold until the first batch decoded
      if (inViewport && pageVisible) {
        if (!raf) {
          last = performance.now();
          raf = requestAnimationFrame(frame);
        }
      } else if (raf) {
        cancelAnimationFrame(raf);
        raf = 0;
      }
    }

    function handleVisibility() {
      pageVisible = !document.hidden;
      syncAnimation();
    }

    if (reduced) {
      // static field: wait for images, then paint once with no motion
      var tries = 0;
      var settle = setInterval(function () {
        tries += 1;
        var ready = images.every(function (im) { return im.complete; });
        if (ready || tries > 40) {
          clearInterval(settle);
          if (!destroyed) paint(0, false);
        }
      }, 100);
    } else {
      if ('IntersectionObserver' in window) {
        intersectionObserver = new IntersectionObserver(function (entries) {
          inViewport = !!entries[0] && entries[0].isIntersecting;
          syncAnimation();
        }, { rootMargin: '120px 0px', threshold: 0.01 });
        intersectionObserver.observe(container);
      }
      document.addEventListener('visibilitychange', handleVisibility);
      syncAnimation();
    }

    return {
      update: function (partial) {
        var k;
        for (k in partial) { if (Object.prototype.hasOwnProperty.call(partial, k)) opts[k] = partial[k]; }
        if (partial && Object.prototype.hasOwnProperty.call(partial, 'stream')) {
          streaming = !!opts.stream && !reduced;
          if (!streaming) started = true;
        }
        if (partial && partial.images) loadImages(partial.images);
      },
      destroy: function () {
        destroyed = true;
        if (streamTimer) { window.clearTimeout(streamTimer); streamTimer = 0; }
        cancelAnimationFrame(raf);
        if (intersectionObserver) intersectionObserver.disconnect();
        document.removeEventListener('visibilitychange', handleVisibility);
        window.removeEventListener('resize', resize);
        if (canvas.parentNode) canvas.parentNode.removeChild(canvas);
      }
    };
  }

  window.SoollaWarp = { mount: mount };
})();
